import { type ComponentPropsWithoutRef, forwardRef, memo, type ReactNode } from 'react';
import { CheckSmallIcon } from '@/icons';
import { StaticRichTextRenderer } from '@/shared/features/rich-text';
import { cn } from '@/utils/tailwind.util';
import { QuizImage } from './QuizImage';

/**
 * QuizMultipleChoice - 객관식 컴포넌트
 * EditableMultiple 구조와 일치하는 읽기 전용 버전
 * @module QuizMultipleChoice
 */

/**
 * 객관식 목록을 위한 컨테이너
 * EditableMultiple 컨테이너 구조와 일치
 */
const Container = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('flex flex-col gap-sm', className)} {...rest}>
        {children}
      </div>;
});
Container.displayName = 'QuizMultipleChoiceContainer';

/**
 * IndicatorContainer - 객관식 선택지 표시를 위한 원형 인디케이터
 * 사용자 답안 상태(정답/오답/미선택)를 시각적으로 표시
 */
const IndicatorContainer = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('flex size-[30px] flex-shrink-0 items-center justify-center rounded-full border-[3px] border-white shadow-xs', className)} {...props} data-sentry-component="IndicatorContainer" data-sentry-source-file="QuizMultipleChoice.tsx">
      {children}
    </div>;
};

/**
 * Item - Compound Component Pattern for Multiple Choice Item Layout
 * 공통 레이아웃 구조를 제공하는 복합 컴포넌트
 */
const ItemRoot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('flex w-full flex-col gap-2xs', className)} {...rest}>
        {children}
      </div>;
});
ItemRoot.displayName = 'QuizMultipleChoiceItemRoot';
const ItemRow = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('relative flex flex-row items-center gap-xs', className)} {...rest}>
        {children}
      </div>;
});
ItemRow.displayName = 'QuizMultipleChoiceItemRow';
const ItemContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('relative flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-2xs', className)} {...rest}>
        {children}
      </div>;
});
ItemContent.displayName = 'QuizMultipleChoiceItemContent';

/**
 * ItemImageContainer - ml-[39px] offset과 QuizImage.Container를 결합한 컨테이너
 * QuizImage.Container의 모든 기능(hover, click)을 유지하면서 레이아웃 offset 제공
 */
const ItemImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({
  children,
  className,
  onClick,
  onKeyDown,
  ...rest
}, ref) => {
  return <div ref={ref} className={cn('ml-[39px]', className)} {...rest}>
      <QuizImage.Container onClick={onClick} onKeyDown={onKeyDown}>
        {children}
      </QuizImage.Container>
    </div>;
});
ItemImageContainer.displayName = 'QuizMultipleChoiceItemImageContainer';
const Indicator = {
  Container: IndicatorContainer
};
const Image = {
  Container: ItemImageContainer,
  Thumbnail: QuizImage.Thumbnail
};
type ItemLayoutProps = {
  content?: string;
  imageUrl?: string;
  imageAlt?: string;
  leading: ReactNode;
  trailing?: ReactNode;
  textClassName?: string;
  imageContainerProps?: ComponentPropsWithoutRef<'div'>;
} & ComponentPropsWithoutRef<'div'>;
const ItemLayout = forwardRef<HTMLDivElement, ItemLayoutProps>(({
  content,
  imageUrl,
  imageAlt = '',
  leading,
  trailing,
  textClassName,
  imageContainerProps,
  className,
  ...rest
}, ref) => {
  const isImageOnly = !!imageUrl && !content;
  const renderImage = () => {
    if (!imageUrl) return null;
    return <QuizImage.Container {...imageContainerProps} className={cn('size-[62px]', imageContainerProps?.className)} data-sentry-element="unknown" data-sentry-component="renderImage" data-sentry-source-file="QuizMultipleChoice.tsx">
          <QuizImage.Thumbnail src={imageUrl} alt={imageAlt} data-sentry-element="unknown" data-sentry-source-file="QuizMultipleChoice.tsx" />
        </QuizImage.Container>;
  };
  const rowContent = <>
        {leading}
        {isImageOnly ? renderImage() : content ? <ItemContent>
            <StaticRichTextRenderer as="div" text={content} className={textClassName} />
          </ItemContent> : null}
      </>;
  return <ItemRoot ref={ref} className={className} {...rest}>
        <ItemRow className={cn('items-start', trailing && 'justify-between')}>
          {trailing ? <>
              <div className="flex min-w-0 flex-1 items-start gap-xs">
                {rowContent}
              </div>
              {trailing}
            </> : rowContent}
        </ItemRow>
        {imageUrl && !isImageOnly && <ItemImageContainer {...imageContainerProps} className={cn('size-[62px]', imageContainerProps?.className)}>
            <QuizImage.Thumbnail src={imageUrl} alt={imageAlt} />
          </ItemImageContainer>}
      </ItemRoot>;
});
ItemLayout.displayName = 'QuizMultipleChoiceItemLayout';
const ItemCompound = Object.assign(ItemRoot, {
  Row: ItemRow,
  Indicator,
  Content: ItemContent,
  Image
});

/**
 * Item - Props 기반 래퍼
 * QuizItemCard 등에서 사용하는 기본 API
 */
const Item = memo(forwardRef<HTMLDivElement, {
  text: string;
  imageUrl?: string;
  isAnswer: boolean;
  choiceNumber?: number;
  /** 보기 텍스트 클래스 덮어쓰기 — 문제 은행처럼 오답을 흐리게 그리는 화면용. */
  textClassName?: string;
} & ComponentPropsWithoutRef<'div'>>((props, ref) => {
  const {
    text,
    imageUrl,
    isAnswer,
    choiceNumber,
    textClassName,
    ...rest
  } = props;
  const indicator = <IndicatorContainer className={isAnswer ? 'bg-green-500' : 'bg-gray-200'}>
        {isAnswer && <CheckSmallIcon size="lg" fill="white" />}
      </IndicatorContainer>;
  return <ItemLayout ref={ref} {...rest} content={text} imageUrl={imageUrl} imageAlt={`Choice ${choiceNumber}`} leading={indicator} textClassName={textClassName ?? 'text-heading-sm-bold'} />;
}));
Item.displayName = 'QuizMultipleChoiceItem';
export const QuizMultipleChoice = {
  Container,
  ItemLayout,
  Item,
  ItemCompound
};